// Performance optimization utilities

// Critical CSS extraction and inline loading
export const inlineCriticalCSS = () => {
  // Move critical above-the-fold styles inline
  const criticalCSS = `
    body { 
      font-family: system-ui, -apple-system, sans-serif; 
      margin: 0; 
      background: hsl(45 50% 99%);
      color: hsl(35 84% 20%);
    }
    .hero-gradient { 
      background: linear-gradient(to bottom, rgb(254 252 232), rgb(254 249 195 / 0.5)); 
    }
    .loading-skeleton {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
    }
    @keyframes loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;
  
  const style = document.createElement('style');
  style.textContent = criticalCSS;
  document.head.appendChild(style);
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const resources = [
    { href: '/lovable-uploads/21feca63-2f1d-4559-89fa-da647484fc76.png', as: 'image' },
  ];
  
  resources.forEach(({ href, as }) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (as === 'image') {
      link.setAttribute('fetchpriority', 'high');
    }
    document.head.appendChild(link);
  });
};

// Remove unused CSS classes (basic implementation)
export const removeUnusedCSS = () => {
  // This would be expanded with a proper CSS tree shaking solution
  // For now, we ensure critical styles are loaded first
  setTimeout(() => {
    const sheets = Array.from(document.styleSheets);
    sheets.forEach(sheet => {
      try {
        const rules = Array.from(sheet.cssRules || []);
        // Mark critical rules for retention
        rules.forEach(rule => {
          if (rule instanceof CSSStyleRule) {
            const selector = rule.selectorText;
            if (selector && !document.querySelector(selector)) {
              // Rule can potentially be removed (would need more sophisticated logic)
            }
          }
        });
      } catch (e) {
        // Cross-origin stylesheets can't be accessed
      }
    });
  }, 5000); // Run after page is fully loaded
};

// Optimize images on the fly
export const optimizeImages = () => {
  const images = document.querySelectorAll('img[data-optimize]');
  
  images.forEach((img: Element) => {
    const imgElement = img as HTMLImageElement;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Add lazy loading and responsive image logic
          const src = imgElement.dataset.src || imgElement.src;
          if (src && !imgElement.src.includes('data:')) {
            imgElement.loading = 'lazy';
            imgElement.decoding = 'async';
          }
          observer.unobserve(imgElement);
        }
      });
    });
    
    observer.observe(imgElement);
  });
};

// Initialize all performance optimizations
export const initPerformanceOptimizations = () => {
  // Run critical optimizations immediately
  inlineCriticalCSS();
  preloadCriticalResources();
  
  // Run non-critical optimizations after page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      optimizeImages();
      removeUnusedCSS();
    });
  } else {
    optimizeImages();
    removeUnusedCSS();
  }
};